/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo } from 'react';
import { UNLICENSED } from '@jira/platform__application-editions/src/edition.tsx';
import { isCMDBEnabledInEnvironment } from '@jira/servicedesk__insight-common-utils-cmdb-fedramp/src/isCMDBEnabledInEnvironment.tsx';
import { isTeamsAppEnabled } from '@jira/platform__teams-app-config/src/index.tsx';
import { isFedrampModerate } from '@atlaskit/atlassian-context/is-fedramp-moderate';
import { useAssetsWorkspaceHost } from '@atlassian/assets-workspace-host/use-assets-workspace-host';
import { getProductContext } from '@atlassian/assets-workspace-host/get-product-context';
import { fg } from '@atlassian/jira-feature-gating';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import {
	CONFIGURABLE_MENU_IDS_AS_STRINGS,
	GLOBAL_APP_MENU_IDS_AS_STRINGS,
} from '../../constants/index.tsx';
import { L1_MENU_ID, type GlobalAppMenuId } from '../../types.tsx';

export type GlobalAppsLicense = {
	goals: boolean;
	assets: boolean;
	confluence: boolean;
	loom: boolean;
	projects: boolean;
	teams: boolean;
	opsgenie: boolean;
};

/**
 * Assets should show when there's a JSM license
 * Goals should show for all other Jira products but not when it's JSM-only
 */
export function useGlobalApps(): GlobalAppsLicense {
	const appEditions = useAppEditions();

	const shouldShowGoals =
		(appEditions.productDiscovery !== UNLICENSED ||
			appEditions.software !== UNLICENSED ||
			// JSM comes with a free core license. Goals don't display with JSM-only, so checking for unlicensed JSM
			(appEditions.core !== UNLICENSED && appEditions.serviceDesk === UNLICENSED)) &&
		(fg('aem_nav_common_core') ? isFeatureEnabled('jira', 'jira-goals-nav') : !isFedrampModerate());

	// cross-unit Assets shortcut visibility.
	const cloudId = useCloudId();
	const appPermissions = useApplicationPermissions();
	const workspaceHostState = useAssetsWorkspaceHost({
		cloudId,
		productContext: getProductContext({ appPermissions }),
		skip: !fg('astral_units_workspace_host_resolver'),
	});

	const isResolverSuccess =
		workspaceHostState.status === 'success' && fg('astral_units_workspace_host_resolver');

	const isAssetsEntitledOnThisSiteOrUnit = isResolverSuccess
		? true
		: appEditions.serviceDesk !== UNLICENSED;

	const shouldShowAssets =
		isAssetsEntitledOnThisSiteOrUnit && fg('assets_as_an_app_v2') && isCMDBEnabledInEnvironment();

	const shouldShowTeams = isTeamsAppEnabled();

	const globalAppShortcuts = useMemo(() => {
		if (appEditions.core !== UNLICENSED) {
			if (appEditions.serviceDesk !== UNLICENSED) {
				return {
					confluence: true,
					loom: true,
					projects: false,
					teams: shouldShowTeams,
					opsgenie: true,
				};
			}

			if (appEditions.productDiscovery !== UNLICENSED) {
				return {
					confluence: true,
					loom: true,
					projects: false,
					teams: shouldShowTeams,
					opsgenie: false,
				};
			}
		} else {
			if (appEditions.serviceDesk !== UNLICENSED) {
				return {
					confluence: false,
					loom: false,
					projects: false,
					teams: shouldShowTeams,
					opsgenie: true,
				};
			}

			if (appEditions.productDiscovery !== UNLICENSED) {
				return {
					confluence: false,
					loom: false,
					projects: false,
					teams: shouldShowTeams,
					opsgenie: false,
				};
			}
		}

		return {
			confluence: true,
			loom: true,
			projects: true,
			teams: shouldShowTeams,
			opsgenie: false,
		};
	}, [appEditions, shouldShowTeams]);

	return useMemo(() => {
		return {
			goals: shouldShowGoals,
			assets: shouldShowAssets,
			...globalAppShortcuts,
		};
	}, [globalAppShortcuts, shouldShowAssets, shouldShowGoals]);
}

export const isSupportedMenuId = (value: string): value is GlobalAppMenuId =>
	CONFIGURABLE_MENU_IDS_AS_STRINGS.includes(value);

export const isGlobalAppMenuId = (value: string): value is GlobalAppMenuId =>
	GLOBAL_APP_MENU_IDS_AS_STRINGS.includes(value);

export const hasGlobalAppLicense = (
	value: string,
	globalAppLicense: GlobalAppsLicense,
): value is GlobalAppMenuId => {
	switch (value) {
		case L1_MENU_ID.GLOBAL_APP_ASSETS:
			return globalAppLicense.assets;
		case L1_MENU_ID.GLOBAL_APP_GOALS:
			return globalAppLicense.goals;
		case L1_MENU_ID.GLOBAL_APP_CONFLUENCE:
			return globalAppLicense.confluence;
		case L1_MENU_ID.GLOBAL_APP_LOOM:
			return globalAppLicense.loom;
		case L1_MENU_ID.GLOBAL_APP_PROJECTS:
			return globalAppLicense.projects;
		case L1_MENU_ID.GLOBAL_APP_TEAMS:
			return globalAppLicense.teams;
		default:
			return false;
	}
};
